import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, ShieldAlert, BadgeCheck, XCircle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * PULA LEARN — public certificate verification.
 * Pre-launch: always shows the strict "no certificates exist yet" notice.
 * Post-accreditation: real lookup returning public-safe fields only.
 */

export default function LearnVerifyPage() {
  const { certId } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(certId || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (id) => {
    if (!id) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get(`/learn/verify/${encodeURIComponent(id.trim())}`);
      setResult(data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || "Verification unavailable — please try again." });
    } finally { setBusy(false); }
  };

  useEffect(() => { if (certId) lookup(certId); /* eslint-disable-next-line */ }, [certId]);

  const submit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      navigate(`/verify/${encodeURIComponent(input.trim())}`, { replace: true });
      lookup(input);
    }
  };

  return (
    <main className="bg-white min-h-[70vh]" data-testid="learn-verify-page">
      <section className="mv-green-bg text-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Verify a Certificate</h1>
          <p className="text-base md:text-lg text-white/85">
            Enter a Pula Learn certificate ID (format PL-YYYY-NNNNNN) to check it is genuine.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-xl mx-auto px-4 md:px-8">
          <form onSubmit={submit} className="flex gap-3 mb-8" data-testid="verify-form">
            <input data-testid="verify-cert-input" value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. PL-2026-000001"
              className="border mv-hairline rounded-lg px-4 py-3 text-sm flex-1" />
            <button type="submit" disabled={busy} data-testid="verify-submit-btn"
              className="btn-green inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60">
              <Search size={16} /> {busy ? "Checking…" : "Verify"}
            </button>
          </form>

          {result && (
            <div data-testid="verify-result">
              {result.error ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-6 flex gap-3">
                  <XCircle size={20} className="shrink-0 text-red-600 mt-0.5" />
                  <p className="text-sm text-red-800">{result.error}</p>
                </div>
              ) : result.pre_launch ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 flex gap-3" data-testid="verify-prelaunch-notice">
                  <ShieldAlert size={20} className="shrink-0 text-amber-600 mt-0.5" />
                  <div>
                    <h2 className="text-sm font-bold text-amber-900 mb-1">
                      Pula Learn has not yet issued any certificates
                    </h2>
                    <p className="text-sm text-amber-800">{result.notice}</p>
                  </div>
                </div>
              ) : result.valid ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6" data-testid="verify-valid">
                  <div className="flex items-center gap-2 mb-4">
                    <BadgeCheck size={20} className="text-emerald-700" />
                    <h2 className="text-sm font-bold text-emerald-900">Valid certificate</h2>
                  </div>
                  <dl className="text-sm text-emerald-900 space-y-1.5">
                    <div><dt className="inline font-semibold">Holder: </dt><dd className="inline">{result.holder_name}</dd></div>
                    <div><dt className="inline font-semibold">Course: </dt><dd className="inline">{result.course_title}</dd></div>
                    <div><dt className="inline font-semibold">Issued: </dt><dd className="inline">{result.issued_at?.slice(0, 10)}</dd></div>
                    <div><dt className="inline font-semibold">CPD hours: </dt><dd className="inline">{result.cpd_hours}</dd></div>
                    <div><dt className="inline font-semibold">CPD Provider No.: </dt><dd className="inline">{result.cpd_provider_number}</dd></div>
                  </dl>
                </div>
              ) : (
                <div className="rounded-xl border border-red-200 bg-red-50 p-6 flex gap-3" data-testid="verify-invalid">
                  <XCircle size={20} className="shrink-0 text-red-600 mt-0.5" />
                  <p className="text-sm text-red-800">
                    {result.status === "revoked"
                      ? "This certificate has been revoked. Contact Sebata@pulalove.com with questions."
                      : result.notice || "No genuine certificate matches this ID."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
